import { getSDK, useStores, type Message } from '@littlebird/sdk';
import { findLinkedActionCardIds, opensTurn } from '@littlebird/ui-utils';
import { useMatchRoute } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';

import { YStack } from '@/components/ui/layout/layout';
import { cn } from '@/lib/utils';

import { useCanvasActionCardPlacements } from '../canvas-action-card-placement-context';

import { ActionCardItem } from './ActionCardItem';
import { ParentCardLine } from './ParentCardLine';

interface MessageActionCardsProps {
  threadId: string;
  assistantMessageId: string;
  isTurnTail: boolean;
  onAskUserComplete?: () => void;
}

/**
 * Turn window used to scope orphan-anchored cards (see below): from the
 * turn's leading user message (or no lower bound if the turn opens the
 * thread) up to, but not including, the next user message after the turn —
 * or +Infinity if the turn is still the latest one.
 */
function getTurnWindow(
  threadMessages: Message[],
  ownIndex: number
): { startAt: Date | null; endAtMs: number } {
  // Own message isn't in the thread at all — no reliable window to bound
  // orphan cards against, so exclude them rather than guess.
  if (ownIndex === -1) {
    return { startAt: null, endAtMs: Number.NEGATIVE_INFINITY };
  }

  let startAt: Date | null = null;
  for (let i = ownIndex - 1; i >= 0; i -= 1) {
    const message = threadMessages[i];
    if (message && opensTurn(message)) {
      startAt = message.createdAt;
      break;
    }
  }

  let endAtMs = Number.POSITIVE_INFINITY;
  for (let i = ownIndex + 1; i < threadMessages.length; i += 1) {
    const message = threadMessages[i];
    if (opensTurn(message)) {
      endAtMs = message.createdAt.getTime();
      break;
    }
  }

  return { startAt, endAtMs };
}

const VisibleMessageActionCards = observer(
  ({
    threadId,
    assistantMessageId,
    onAskUserComplete,
  }: Omit<MessageActionCardsProps, 'isTurnTail'>) => {
    // Cards arrive by sync under the same MobX store, so React Compiler must not cache these filters
    'use no memo';
    const { messages } = useStores();
    const threadMessages = messages.getMessagesByThreadId(threadId);
    const ownIndex = threadMessages.findIndex(
      (message) => message.id === assistantMessageId
    );
    const turnMessageIds: string[] = [];
    const turnText: string[] = [];
    for (let i = ownIndex; i >= 0; i -= 1) {
      const message = threadMessages[i];
      if (!message || opensTurn(message)) break;
      if (message.role === 'assistant') {
        turnMessageIds.unshift(message.id);
        turnText.push(message.content);
      }
    }
    if (ownIndex === -1) turnMessageIds.push(assistantMessageId);
    const { canvasOnlyLocalIds, isLoading: isCanvasPlacementLoading } =
      useCanvasActionCardPlacements();

    if (isCanvasPlacementLoading) return null;

    const store = getSDK().stores.actionCards;
    const anchoredCards = turnMessageIds.flatMap((id) =>
      store.getByAssistantMessageId(id)
    );

    // Subagent-created cards sync with this chat's id but anchor to an
    // assistant message that lives in the subagent's own internal chat, so
    // the turn walk above never finds them. Recover them by chatId, scoped
    // to this turn's time window so each run's cards land on that run.
    const threadMessageIds = new Set(
      threadMessages.map((message) => message.id)
    );
    const { startAt: turnStartAt, endAtMs: turnEndAtMs } = getTurnWindow(
      threadMessages,
      ownIndex
    );
    const anchoredCardIds = new Set(anchoredCards.map((card) => card.id));
    const orphanCards = store
      .getByChatId(threadId)
      .filter((card) => {
        if (anchoredCardIds.has(card.id)) return false;
        if (!card.assistantMessageId) return false;
        if (threadMessageIds.has(card.assistantMessageId)) return false;
        const createdAtMs = card.createdAt.getTime();
        if (turnStartAt !== null && createdAtMs < turnStartAt.getTime())
          return false;
        return createdAtMs < turnEndAtMs;
      })
      .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

    const linkedCardIds = findLinkedActionCardIds(turnText.join('\n'));
    const seenCardIds = new Set<string>();
    const cards = [...anchoredCards, ...orphanCards].filter((card) => {
      if (linkedCardIds.has(card.localId)) return false;
      if (canvasOnlyLocalIds.has(card.localId)) return false;
      if (seenCardIds.has(card.id)) return false;
      seenCardIds.add(card.id);
      return true;
    });

    if (cards.length === 0) return null;

    return (
      <YStack className="gap-3">
        {cards.map((card) => (
          <YStack
            className={cn(
              'gap-1',
              card.actionType === 'ask_user' && 'w-full max-w-[85%]'
            )}
            key={card.id}
          >
            <ParentCardLine card={card} />
            <ActionCardItem card={card} onAskUserComplete={onAskUserComplete} />
          </YStack>
        ))}
      </YStack>
    );
  }
);

export const MessageActionCards = observer((props: MessageActionCardsProps) => {
  const matchRoute = useMatchRoute();
  const isSharedChat = !!matchRoute({ to: '/shared-chat/$id' });

  if (isSharedChat || !props.isTurnTail) return null;

  return (
    <VisibleMessageActionCards
      threadId={props.threadId}
      assistantMessageId={props.assistantMessageId}
      onAskUserComplete={props.onAskUserComplete}
    />
  );
});
